/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Input,
  Modal,
  message,
  Tree,
  Card,
  TreeSelect,
  Alert,
} from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { formatReceived } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import ModalConfirmation from 'components/ModalConfirmation';
import Select from 'components/Select';
import { getCuentasContables, getEstadosGlobales, getFuentesDeFinanciamientoContabilidad } from 'api/catalogos/index';
import { getConfiguracionesDeMascaras } from 'api/catalogos/ConfiguracionesDeMascaras';
import {
  getNivelesDeEstudios,
  postNivelDeEstudio,
  deleteNivelDeEstudio,
  patchNivelDeEstudio,
  getNivelDeEstudio,
  autorizarNivelDeEstudio,
} from 'api/catalogos/nivelesDeEstudios';
import { checkToken } from 'api/utils';
import {
  ESTATUS_ACTIVO,
  ESTATUS_INACTIVO,
  ESTATUS_AUTORIZADO,
} from 'utils/constants';
import { patchCuentaContable, postCuentaContable } from 'api/catalogos/CuentaContable';
import {
  autorizarCuentasContable,
  getCuentaContable,
  getCuentasContablesAfeccionesPresupuestales,
  getCuentasContablesClasificador,
  getCuentasContablesEgresos,
  getCuentasContablesIngresos,
  getCuentasContablesNaturaleza,
  getCuentasContablestipos,
} from 'api/catalogos/CuantasContables';
import makeTree from 'utils/makeTree';

export const permissionCuentaContable = {
  permissionModel: 'cuentacontable',
  catalogsModels: [],
  tabsModels: [],
};

const baseURI = 'contabilidad/cuentas-contables';

const CatalogoContable = ({ permission }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [visibleAutorizar, setVisibleAutorizar] = useState(false);
  const [selectedData, setSelectedData] = useState({});
  const [visibleConfirmInactivo, setVisibleConfirmInactivo] = useState(false);
  const [disableFields, setDisableFields] = useState(false);
  const [clasificaciones, setClasificaciones] = useState([]);
  const [fuentesFinanciamiento, setFuentesFinanciamiento] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [naturalezas, setNaturalezas] = useState([]);
  const [afectaciones, setAfectaciones] = useState([]);
  const [momentosEgresos, setMomentosEgresos] = useState([]);
  const [momentosIngresos, setMomentosIngresos] = useState([]);
  const [cuentasPlanas, setCuentasPlanas] = useState([]);
  const [mascaraContable, setMascaraContable] = useState(null);

  // Estados para el buscador global
  const [searchText, setSearchText] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [showColumnFilterWarning, setShowColumnFilterWarning] = useState(false);

  const formInstances = [form];

  const removeEmptyChildren = (node) => {
    if (!node.children || node.children.length === 0) {
      return { ...node, children: undefined };
    }

    return {
      ...node,
      children: node.children.map(removeEmptyChildren),
    };
  };

  const onClickTitle = (e) => {
    const element = e.target;
    if (element) {
      let parent = element.parentElement;
      parent = parent ? parent.parentElement : null;
      if (parent) {
        const clickableSpan = parent.previousSibling;
        if (clickableSpan) {
          clickableSpan.click();
        }
      }
    }
  };

  const renderTitle = (title, bold) => (
    // eslint-disable-next-line
    <span onClick={onClickTitle} className={bold ? 'bold' : ''}>
      {title}
    </span>
  );

  const normalizeFinan = (_data = [], level = 0, visited = new Set()) => _data.map(
    (item) => {
      const clone = { ...item };
      const uniqueId = clone.id ?? clone.clave;

      if (visited.has(uniqueId)) {
        return null;
      }

      visited.add(uniqueId);

      const children = Array.isArray(clone.children)
        ? normalizeFinan(clone.children, level + 1, new Set(visited))
        : [];

      return {
        ...clone,
        key: clone.id,
        value: clone.id,
        title: renderTitle(`${clone.clave} - ${clone.nombre}`, children.length > 0),
        disabled: item.seleccionable === false,
        children: children.length ? children : undefined,
      };
    },
  ).filter(Boolean);

  const formatDataTree = (_data = [], excludeLvl3 = false, currLvl = 1) => _data.map((item) => {
    const clone = { ...item };

    const children = formatDataTree(
      !excludeLvl3 || currLvl !== 2 ? clone.children || [] : [],
      excludeLvl3,
      currLvl + 1,
    );

    const node = {
      key: clone.key || clone.id?.toString(),
      value: clone.value || clone.id?.toString(),
      title: clone.title,
      disabled: !clone.seleccionable,
      children, // mantener siempre
    };

    return node;
  });

  const normalize = (_data) => {
    const nodeMap = new Map();

    _data.forEach((node) => {
      nodeMap.set(node.id, {
        ...node,
        children: [],
        key: `${node.id}`,
      });
    });

    const roots = [];
    _data.forEach((node) => {
      const currentNode = nodeMap.get(node.id);
      if (node.cuenta_padre) {
        const parent = nodeMap.get(node.cuenta_padre);
        if (parent) {
          parent.children.push(currentNode);
        }
      } else {
        roots.push(currentNode);
      }
    });

    return roots.map(removeEmptyChildren);
  };

  const findNodeById = (nodes, id) => {
    let result = null;

    nodes.some((node) => {
      if (String(node.id) === id) {
        result = node;
        return true;
      }
      if (node.children && node.children.length) {
        const found = findNodeById(node.children, id);
        if (found) {
          result = found;
          return true;
        }
      }
      return false;
    });

    return result;
  };

  const flattenTree = (nodes) => {
    const flat = [];
    nodes.forEach((node) => {
      flat.push({ id: node.id, nombre: node.nombre });
      if (node.children) {
        flat.push(...flattenTree(node.children));
      }
    });
    return flat;
  };

  const TIPO_CUENTA_MAYOR = 4;

  const mapToTreeData = (nodes, level = 1) => nodes.map((node) => {
    // const isLeaf = !node.children || node.children.length === 0;
    const isDeepEnough = level >= 5;
    const esCuentaMayor = node.tipo === TIPO_CUENTA_MAYOR;
    // !isLeaf
    return {
      title: `${node.clave_visible || node.clave} - ${node.nombre}`,
      value: node.id,
      key: node.id,
      disabled: !isDeepEnough && !esCuentaMayor,
      children: node.children
        ? mapToTreeData(node.children, level + 1)
        : undefined,
    };
  });

  const fetchData = async () => {
    setLoading(true);
    const _data = await getCuentasContables();
    const normalized = normalize(_data);
    setData(normalized);
    setFilteredData(normalized);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;

    const fetchAll = async () => {
      setLoading(true);
      await checkToken();

      const [
        _estados,
        _data,
        _clasificaciones,
        _tipos,
        _naturalezas,
        _afectaciones,
        _momentosEgresos,
        _momentosIngresos,
        _fuentesFinanciamiento,
        _mascaras,
      ] = await Promise.all([
        getEstadosGlobales(),
        getCuentasContables(),
        getCuentasContablesClasificador(),
        getCuentasContablestipos(),
        getCuentasContablesNaturaleza(),
        getCuentasContablesAfeccionesPresupuestales(),
        getCuentasContablesEgresos(),
        getCuentasContablesIngresos(),
        getFuentesDeFinanciamientoContabilidad(),
        getConfiguracionesDeMascaras({ tipo: 1 }),
      ]);

      if (mounted) {
        setEstados(_estados);
        const normalized = normalize(_data);
        setData(normalized);
        setFilteredData(normalized);
        setClasificaciones(_clasificaciones);
        setTipos(_tipos);
        setNaturalezas(_naturalezas);
        setAfectaciones(_afectaciones);
        setMomentosEgresos(_momentosEgresos);
        setMomentosIngresos(_momentosIngresos);
        setCuentasPlanas(flattenTree(normalized));
        if (_mascaras && _mascaras.length > 0) {
          setMascaraContable(_mascaras[0]);
        }
        const fuentesFTree = makeTree(_fuentesFinanciamiento);
        const normalizedFinan = normalizeFinan(fuentesFTree);
        setFuentesFinanciamiento(formatDataTree(normalizedFinan));
        setLoading(false);
      }
    };

    fetchAll();
    return () => { mounted = false; };
  }, []);

  // Filtrado global del árbol
  useEffect(() => {
    if (!searchText.trim()) {
      setFilteredData(data);
      return;
    }

    const lowerSearch = searchText.toLowerCase().trim();

    const flattenAndFilter = (nodes) => {
      let results = [];
      nodes.forEach((node) => {
        const matches = node.clave?.toLowerCase().includes(lowerSearch)
          || node.nombre?.toLowerCase().includes(lowerSearch)
          || node.descripcion?.toLowerCase().includes(lowerSearch);

        if (matches) {
          results.push({ ...node, children: undefined });
        }

        if (node.children) {
          results = results.concat(flattenAndFilter(node.children));
        }
      });
      return results;
    };

    const newFiltered = flattenAndFilter(data);
    setFilteredData(newFiltered);
  }, [searchText, data]);

  const onCancel = () => {
    setVisible(false);
    setVisibleAutorizar(false);
    setDisableFields(false);
    setSelectedRowKeys([]);
    setSelectedData({});
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;

    const match = responseData || findNodeById(data, key);
    if (!match) {
      setLoading(false);
      return;
    }

    try {
      const cuenta = await getCuentaContable(match.id);
      if (cuenta) {
        form.resetFields();

        form.setFieldsValue({
          ...formatReceived(cuenta),
          cuenta_contable: cuenta.id,
        });

        setDisableFields(cuenta?.estados_globales !== ESTATUS_ACTIVO);
        setVisible(true);
      }
    } catch (err) {
      // getCuentaContable ya avisa al usuario vía onError; aquí solo dejamos
      // rastro para diagnóstico de cualquier otro error en este bloque.
      // eslint-disable-next-line no-console
      console.error('[Claves Contables] error al cargar la cuenta para editar', err);
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const payload = {
        ...values,
        clasificador_de_objeto_gasto: null,
      };
      const [key] = selectedRowKeys;
      const saved = await (key ? patchCuentaContable(key, payload) : postCuentaContable(payload));
      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading, formInstances);
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteNivelDeEstudio(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.key]);
    setSelectedData({ ...record });
    form.setFieldsValue(record);
  };

  const segmentosMascara = mascaraContable
    ? mascaraContable.mascara.split(mascaraContable.separador).map((s) => s.length)
    : [];
  const separadorMascara = mascaraContable ? mascaraContable.separador : '-';
  const maxDigitosMascara = segmentosMascara.reduce((a, b) => a + b, 0);

  const formatearClave = (raw) => {
    if (!segmentosMascara.length) return raw;
    const soloDigitos = raw.replace(/[^0-9]/g, '').slice(0, maxDigitosMascara);
    const partes = [];
    let idx = 0;
    for (let i = 0; i < segmentosMascara.length; i += 1) {
      if (idx >= soloDigitos.length) break;
      partes.push(soloDigitos.slice(idx, idx + segmentosMascara[i]));
      idx += segmentosMascara[i];
    }
    return partes.join(separadorMascara);
  };

  const handleClaveChange = (e) => {
    const raw = e.target.value;
    if (!segmentosMascara.length) return;
    const formateada = formatearClave(raw);
    form.setFieldsValue({ clave: formateada });
  };

  const validarFormatoClave = (_, value) => {
    if (!value || !segmentosMascara.length) return Promise.resolve();
    const partes = value.split(separadorMascara);
    if (partes.length !== segmentosMascara.length) {
      return Promise.reject(
        new Error(`La clave debe tener ${segmentosMascara.length} segmentos separados por "${separadorMascara}" (${mascaraContable.mascara})`),
      );
    }
    for (let i = 0; i < segmentosMascara.length; i += 1) {
      if (partes[i].length !== segmentosMascara[i] || !/^\d+$/.test(partes[i])) {
        return Promise.reject(
          new Error(`El segmento ${i + 1} debe tener ${segmentosMascara[i]} dígitos (${mascaraContable.mascara})`),
        );
      }
    }
    return Promise.resolve();
  };

  const handleKeyPress = (e) => {
    if (/\d/.test(e.key)) {
      e.preventDefault();
    }
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    form.resetFields();
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    const [key] = selectedRowKeys;
    if (key) {
      setVisibleAlert(true);
    } else {
      setVisibleAlert(false);
      message.warn('Este campo no se puede borrar');
    }
  };

  const onConfirmAutorizar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se ha seleccionado una cuenta contable');
        return;
      }
      const saved = await autorizarCuentasContable(key);
      if (saved) {
        await fetchData();
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const columns = [
    {
      titleText: 'Cuenta Contable',
      dataIndex: 'clave',
      key: 'clave',
      width: 400,
    },
    {
      titleText: 'Clave Visible',
      dataIndex: 'clave_visible',
      key: 'clave_visible',
      width: 200,
    },
    {
      titleText: 'Nombre',
      dataIndex: 'nombre',
      key: 'nombre',
      width: 300,
    },
    {
      titleText: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 800,
    },
    {
      titleText: 'Nivel',
      dataIndex: 'nivel',
      key: 'nivel',
      width: 100,
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 200,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const handleCuentaChange = async (value) => {
    if (!value) return;

    try {
      const cuenta = await getCuentaContable(value);
      if (cuenta) {
        form.setFieldsValue({
          nivel: cuenta.nivel,
          posicion_cuenta_mayor: cuenta.posicion_cuenta_mayor,
          clasificacion: cuenta.clasificacion,
          naturaleza: cuenta.naturaleza,
          tipo: cuenta.tipo,
          afectacion_presupuestal: cuenta.afectacion_presupuestal,
          momento_presupuestal_de_ingreso: cuenta.momento_presupuestal_de_ingreso,
          momento_presupuestal_de_egreso: cuenta.momento_presupuestal_de_egreso,
          estados_globales: cuenta.estados_globales,
        });
      }
    } catch (err) {
      // getCuentaContable ya avisa al usuario vía onError; aquí solo dejamos
      // rastro para diagnóstico de cualquier otro error en este bloque.
      // eslint-disable-next-line no-console
      console.error('[Claves Contables] error al autocompletar datos de la cuenta', err);
    }
  };

  const onTableChange = (pagination, filters) => {
    const hasFilters = Object.values(filters).some((vals) => vals && vals.length > 0);
    setShowColumnFilterWarning(hasFilters);
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!open ? (
          <>
            <Row style={{ marginBottom: 16 }}>
              <Col span={24}>
                <Input.Search
                  placeholder="Buscar por clave o nombre"
                  allowClear
                  enterButton={false}
                  size="large"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onSearch={(value) => setSearchText(value)}
                  style={{ maxWidth: 500 }}
                />
              </Col>
            </Row>

            {showColumnFilterWarning && (
              <Alert
                message="En esta página usa el buscador superior para filtrar (incluye subniveles). Los filtros por columna están deshabilitados."
                type="info"
                showIcon
                closable
                onClose={() => setShowColumnFilterWarning(false)}
                style={{ marginBottom: 16 }}
              />
            )}

            <Table
              data={filteredData}
              cols={columns}
              customDataArbolado={filteredData}
              rowSelection={rowSelection}
              handleOnRowClick={handleOnRowClick}
              controls={{
                onClickAdd,
                onClickEdit: () => onClickEdit(),
                onClickDelete,
              }}
              customActions={[
                {
                  onClick: () => setVisibleAutorizar(true),
                  icon: <CheckCircleOutlined />,
                  text: 'Autorizar',
                  disabledBySelection: true,
                  disabled: selectedData?.estados_globales !== ESTATUS_ACTIVO,
                  className: 'success',
                },
              ]}
              disableDelete={selectedData?.estados_globales === ESTATUS_AUTORIZADO}
              permission={permission}
              allowImport
              allowExportPDF={false}
              baseURI={baseURI}
              filterNested
              allowSearch={false}
              onChange={onTableChange}
            />
          </>
        ) : (
          <Card
            className="form-container"
            title={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Cuenta Contable`}
            extra={(
              <FormSubmitControls
                onFinish={form.submit}
                onCancel={onCancel}
                loading={loading}
                onFinishText="Guardar"
              />
            )}
          >
            <Form
              layout="vertical"
              form={form}
              onFinish={onFinish}
              initialValues={{ estados_globales: 1 }}
            >
              <Row gutter={16}>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="cuenta_padre"
                    label="Cuenta Superior"
                    rules={[{ required: true, message: 'Selecciona una cuenta contable' }]}
                    hasFeedback
                  >
                    <TreeSelect
                      showSearch
                      allowClear
                      placeholder="Busca por clave o nombre"
                      treeNodeFilterProp="title"
                      style={{ width: '100%' }}
                      dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                      treeData={mapToTreeData(data)}
                      onChange={handleCuentaChange}
                      filterTreeNode={(inputValue, treeNode) => {
                        if (!inputValue) return true;

                        const search = inputValue.toLowerCase().trim();
                        const title = (treeNode.title?.props?.children || treeNode.title || '').toString().toLowerCase();

                        return title.includes(search);
                      }}
                      treeDefaultExpandAll={false}
                      virtual
                      dropdownMatchSelectWidth={false}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="clave"
                    label="Cuenta Contable"
                    rules={[
                      { required: true },
                      { validator: validarFormatoClave },
                    ]}
                  >
                    <Input
                      allowClear
                      onChange={handleClaveChange}
                      placeholder={mascaraContable ? mascaraContable.mascara : ''}
                      maxLength={mascaraContable ? mascaraContable.mascara.length : undefined}
                    />
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item name="clave_visible" label="Clave Visible">
                    <Input disabled />
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item name="descripcion" label="Descripción" rules={[{ required: true }]}>
                    <Input allowClear />
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item name="nombre" label="Nombre" rules={[{ required: true }]}>
                    <Input allowClear />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="nivel" label="Nivel">
                    <Input type="number" disabled />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="posicion_cuenta_mayor" label="Posición Cuenta Mayor">
                    <Input allowClear disabled />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="clasificacion" label="Clasificación">
                    <Select labelProp="nombre" keyProp="id" dataSource={clasificaciones} allowClear disabled />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="naturaleza" label="Naturaleza">
                    <Select labelProp="nombre" keyProp="id" dataSource={naturalezas} allowClear disabled />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="tipo" label="Tipo">
                    <Select labelProp="nombre" keyProp="id" dataSource={tipos} allowClear />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="afectacion_presupuestal" label="Afectación Presupuestal">
                    <Select labelProp="nombre" keyProp="id" dataSource={afectaciones} allowClear />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="momento_presupuestal_de_ingreso" label="Momento Presupuestal Ingresos">
                    <Select labelProp="nombre" keyProp="id" dataSource={momentosIngresos} allowClear />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="momento_presupuestal_de_egreso" label="Momento Presupuestal Egresos">
                    <Select labelProp="nombre" keyProp="id" dataSource={momentosEgresos} allowClear />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="fuente_de_financiamiento" label="Fuente de Financiamiento">
                    <TreeSelect
                      showSearch
                      treeNodeFilterProp="title"
                      style={{ width: '100%' }}
                      dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                      treeData={fuentesFinanciamiento}
                      allowClear
                    // disabled={!afectaPresupuesto || estatusAutorizado || estatusInactivo}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="estados_globales" label="Estado">
                    <Select labelProp="descripcion" keyProp="id" dataSource={estados} disabled />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>
        )}

        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`el registro ${form.getFieldValue('nombre')}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={onConfirmAutorizar}
          ConfirmAutorize
          onCancel={() => setVisibleAutorizar(false)}
          open={visibleAutorizar}
          contentTitle={`Autorizar la Cuenta Contable ${form.getFieldValue('nombre')}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default CatalogoContable;
